import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MonitorSmartphone } from "lucide-react";
import { useState } from "react";
import type { AdminSessionRecord } from "@hostdock/types";
import {
  Badge, Button, ConfirmDialog, DataTable, describeUserAgent, EmptyState, formatDateTime, formatRelative, PageHeader, Pagination, Panel, Select, toast,
} from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";

const PAGE_SIZE = 50;

export function SessionsPage() {
  return (
    <RequirePermission anyOf={["SECURITY_MANAGE"]}>
      <Sessions />
    </RequirePermission>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const { values, page, set } = useListParams(["kind"] as const);
  const [target, setTarget] = useState<AdminSessionRecord | null>(null);
  const q = useQuery({ queryKey: ["sessions", values, page], queryFn: () => api.sessions.list({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData });
  const revoke = useMutation({ meta: { inlineError: true },
    mutationFn: (s: AdminSessionRecord) => api.sessions.revoke(s.id),
    onSuccess: () => {
      toast.success("Session revoked");
      setTarget(null);
      void qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  return (
    <>
      <PageHeader title="Sessions" description="Signed-in admin and customer sessions that have not expired." />
      <Panel flush>
        <div className="flex items-center gap-2 border-b border-line p-3">
          <Select value={values.kind} onChange={(e) => set({ kind: e.target.value })} className="w-44" aria-label="Filter by account type">
            <option value="">All accounts</option>
            <option value="ADMIN">Admins</option>
            <option value="CUSTOMER">Customers</option>
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(s) => s.id}
          empty={<EmptyState icon={MonitorSmartphone} title="No active sessions" />}
          columns={[
            {
              key: "user",
              header: "Account",
              cell: (s) => (
                <div className="min-w-0">
                  <p className="flex items-center gap-2 truncate font-medium">
                    {s.user.name}
                    <Badge tone={s.user.kind === "ADMIN" ? "accent" : "info"}>{s.user.kind === "ADMIN" ? "Admin" : "Customer"}</Badge>
                    {s.current && <Badge>This session</Badge>}
                  </p>
                  <p className="truncate text-[12px] text-muted">{s.user.email}</p>
                </div>
              ),
            },
            { key: "device", header: "Device", cell: (s) => describeUserAgent(s.userAgent), hideOnMobile: true },
            { key: "ip", header: "IP", cell: (s) => <span className="font-mono text-[12.5px]">{s.ip ?? "—"}</span>, hideOnMobile: true },
            { key: "seen", header: "Last active", cell: (s) => <span title={formatDateTime(s.lastSeenAt)}>{formatRelative(s.lastSeenAt)}</span> },
            { key: "expires", header: "Expires", cell: (s) => <span className="text-muted">{formatDateTime(s.expiresAt)}</span>, hideOnMobile: true },
            {
              key: "actions",
              header: "",
              className: "text-right",
              cell: (s) => (s.current ? null : <Button size="sm" variant="danger-ghost" onClick={() => setTarget(s)}>Revoke</Button>),
            },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      <ConfirmDialog
        open={target !== null}
        onOpenChange={(o) => !o && setTarget(null)}
        title="Revoke session?"
        description={target ? `${target.user.email} will be signed out on ${describeUserAgent(target.userAgent)}.` : undefined}
        confirmLabel="Revoke"
        loading={revoke.isPending}
        error={revoke.error}
        onConfirm={() => target && revoke.mutate(target)}
      />
    </>
  );
}
